import { useQuery } from "@tanstack/react-query";
import { AxiosInstance } from "axios";
import { USER_API } from "config";
import { normalizeId } from "utils";
import { budgetKeys } from "queries";
import { BudgetI, BudgetVersions } from "components/dashboard/body/projects/panels/budget/types";

interface UseBudgetListResult {
  budgetData: BudgetI | null | undefined;
  budgetId: string | undefined;
  isBudgetLoading: boolean;
  isBudgetError: boolean;
  budgetError: Error | null;
  /** Any request in flight, including Try again after a failure. */
  isBudgetFetching: boolean;
  refetchBudget: () => Promise<any>;
}

interface UseBudgetListParams {
  projectId: string | number;
  documentId: string | number;
  axios: AxiosInstance;
  hasValidIds: boolean;
  budgetStatus: boolean;
  isBudgetRunInFlight: boolean;
  enabled: boolean;
}

/**
 * The version every budget page loads and edits, and the one the version list treats as live:
 * the primary version, or, when a budget has none, the oldest. Saving a scenario adds a newer
 * copy, so it never changes which version this picks. Ids only grow, so the lowest is oldest.
 */
export const getLiveBudgetVersionId = (versions: BudgetVersions[]): number | undefined => {
  const primary = versions.find((version) => version.primary && version.budget_id != null);
  if (primary) return primary.budget_id;
  const ids = versions.map((version) => version.budget_id).filter((id) => id != null);
  return ids.length > 0 ? Math.min(...ids) : undefined;
};

export const useBudgetList = ({
  projectId,
  documentId,
  axios,
  hasValidIds,
  budgetStatus,
  isBudgetRunInFlight,
  enabled,
}: UseBudgetListParams): UseBudgetListResult => {
  const pid = normalizeId(projectId);
  const did = normalizeId(documentId);

  const budgetQuery = useQuery<{ budget: BudgetI | null; budgetId: string | undefined }>({
    queryKey: budgetKeys.summary(pid, did),
    queryFn: async () => {
      if (!hasValidIds || !projectId || !documentId) throw new Error("IDs not ready");
      const budgetVersionsResponse = await axios.get<BudgetVersions[]>(
        USER_API.getBudgetVersions(pid, did)
      );
      const budgetVersions = Array.isArray(budgetVersionsResponse)
        ? budgetVersionsResponse
        : budgetVersionsResponse.data || [];
      const budgetVersionId = getLiveBudgetVersionId(budgetVersions)?.toString();
      if (!budgetVersionId) {
        return { budget: null, budgetId: undefined };
      }
      const budgetResponse = await axios.get<BudgetI>(
        USER_API.getBudget(pid, did, budgetVersionId)
      );
      const budget = (budgetResponse as any).data || budgetResponse;
      return { budget: budget as BudgetI, budgetId: budgetVersionId };
    },
    enabled: enabled && hasValidIds && budgetStatus,
    refetchInterval: isBudgetRunInFlight ? 5000 : false,
    refetchIntervalInBackground: isBudgetRunInFlight,
  });

  return {
    budgetData: budgetQuery.data?.budget,
    budgetId: budgetQuery.data?.budgetId,
    // A first fetch in flight. Not `isLoading`: in React Query v4 a disabled query (no budget
    // status row yet) stays in status "loading" forever, which kept pages on a loader.
    isBudgetLoading: budgetQuery.isInitialLoading,
    isBudgetError: budgetQuery.isError,
    budgetError: budgetQuery.error as Error | null,
    isBudgetFetching: budgetQuery.isFetching,
    refetchBudget: budgetQuery.refetch,
  };
};
